import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, User, Mail, Lock, Building2, Home, ArrowRight, CheckCircle, MapPin } from 'lucide-react';
import { authAPI } from '../services/api';
import api from '../services/api';
import { Logo } from '../components/Logo';
import { PlanSelector, handlePlanResult } from '../components/PlanSelector';
import type { PlanChoice, PlanResult } from '../components/PlanSelector';

export default function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    unit: '',
    condoId: '',           // selected condo ID (empty = no condo)
    inviteCode: '',        // condo invitation code (required for membership registration)
    condoSearch: '',       // search text for condo dropdown
    street: '', number: '', neighborhood: '', locality: '', postal_code: '', country: '',
  });
  const [availableCondos, setAvailableCondos] = useState<any[]>([]);
  const [condoDropdownOpen, setCondoDropdownOpen] = useState(false);
  const [selectedCondoName, setSelectedCondoName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false); // track if submit was attempted
  const [accountType, setAccountType] = useState<'personal' | 'condo'>('personal');
  const [unitSetup, setUnitSetup] = useState(false);
  const [planChoice, setPlanChoice] = useState<PlanChoice>('free');
  const [planNote, setPlanNote] = useState('');
  // 'Regístrate por WhatsApp' button (2026-10-07, Jaime-approved): hidden while
  // the feature flag 'whatsapp_signup_button' is beta (is_beta=t in
  // feature_flags); visible only when a super_admin toggles it live.
  const [waVisible, setWaVisible] = useState(false);

  // Fetch available condos when dropdown opens or search changes
  // Uses the public /condos/search endpoint (no auth required) which supports
  // case-insensitive partial matching (ILIKE '%query%').
    useEffect(() => {
    api.get('/whatsapp-registration/public-config')
      .then((res) => setWaVisible(!!res.data?.data?.visible))
      .catch(() => setWaVisible(false));
  }, []);

useEffect(() => {
    if (!condoDropdownOpen) return;
    const fetchCondos = async () => {
      try {
        // Primary: use public search endpoint (no auth needed)
        const params = formData.condoSearch ? { q: formData.condoSearch } : {};
        const res = await api.get('/condos/search', { params });
        const data = res.data?.data ?? res.data;
        setAvailableCondos(Array.isArray(data) ? data : data?.condos || []);
      } catch (err) {
        // Fallback to /condos/available (requires auth)
        try {
          const params = formData.condoSearch ? { search: formData.condoSearch } : {};
          const res = await api.get('/condos/available', { params });
          setAvailableCondos(res.data?.data?.condos || []);
        } catch {
          setAvailableCondos([]);
        }
      }
    };
    const timer = setTimeout(fetchCondos, 200); // debounce
    return () => clearTimeout(timer);
  }, [condoDropdownOpen, formData.condoSearch]);

  // Validation helpers
  const validateName = (val: string) => {
    if (!val.trim()) return t('register.validation.nameRequired');
    if (val.trim().length < 2) return t('register.validation.nameMin');
    return '';
  };

  const validateEmail = (val: string) => {
    if (!val.trim()) return t('register.validation.emailRequired');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val.trim())) return t('register.validation.emailInvalid');
    return '';
  };

  const validatePassword = (val: string) => {
    if (!val) return t('register.validation.passwordRequired');
    if (val.length < 6) return t('register.validation.passwordMin');
    return '';
  };

  const validateConfirmPassword = (val: string) => {
    if (!val) return t('register.validation.confirmRequired');
    if (val !== formData.password) return t('register.validation.passwordsMismatch');
    return '';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear the generic error banner when user starts editing
    if (error) setError('');
  };

  const selectCondo = (condo: any) => {
    setFormData({ ...formData, condoId: condo.id });
    setSelectedCondoName(condo.name);
    setCondoDropdownOpen(false);
  };

  const clearCondo = () => {
    // F-1 fix: only clear condo-related state; preserve standalone-typed unit.
    setFormData({ ...formData, condoId: '', inviteCode: '' });
    setSelectedCondoName('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);

    // Run all validations
    const nameErr = validateName(formData.name);
    const emailErr = validateEmail(formData.email);
    const passwordErr = validatePassword(formData.password);
    const confirmErr = validateConfirmPassword(formData.confirmPassword);
    const inviteCodeErr = accountType === 'condo' && formData.condoId && !formData.inviteCode.trim()
      ? t('register.validation.inviteCodeRequired')
      : '';
    // Standalone unit (spec E7): backend requires full address + country.
    const wantsStandalone = unitSetup && accountType === 'personal';
    const standaloneErr = wantsStandalone
      ? (!formData.unit.trim() ? t('register.validation.unitRequired')
        : !formData.street.trim() ? t('register.validation.streetRequired')
        : !formData.number.trim() ? t('register.validation.numberRequired')
        : !formData.neighborhood.trim() ? t('register.validation.neighborhoodRequired')
        : !formData.locality.trim() ? t('register.validation.localityRequired')
        : !formData.postal_code.trim() ? t('register.validation.postalCodeRequired')
        : !formData.country.trim() ? t('register.validation.countryRequired')
        : '')
      : '';

    if (nameErr || emailErr || passwordErr || confirmErr || inviteCodeErr || standaloneErr) {
      // Set the generic error banner with the first error for accessibility
      setError(nameErr || emailErr || passwordErr || confirmErr || inviteCodeErr || standaloneErr);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const registerRequest = authAPI.register({
        fullName: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        ...(unitSetup || (accountType === 'condo' && formData.condoId) ? {
          unitSetup: true,
          unit: formData.unit || undefined,
          unitLabel: formData.unit || undefined,
          planChoice,
          ...(accountType === 'condo' && formData.condoId ? {
            condoId: formData.condoId, membershipRequired: true, inviteCode: formData.inviteCode.trim() || undefined,
          } : {
            addressData: { street: formData.street, number: formData.number, neighborhood: formData.neighborhood, locality: formData.locality, postal_code: formData.postal_code, country: formData.country },
            country: formData.country,
            membershipRequired: false,
          }),
        } : { membershipRequired: false }),
      });
      const res = await Promise.race([
        registerRequest,
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error(t('register.error.timeout'))), 15000)),
      ]);
      // Registration-time plan result (spec E6): redirect to hosted checkout when
      // payment is required; otherwise surface covered/trial/skipped notes.
      const plan: PlanResult | undefined = res?.data?.data?.plan ?? undefined;
      if (plan?.status === 'checkout_required' && plan?.checkoutUrl) {
        handlePlanResult(plan); // navigates away to the secure payment page
        return;
      }
      const note = handlePlanResult(plan);
      if (note) setPlanNote(note);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || t('register.error.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-full bg-gradient-to-br from-[#36CCD8]/5 to-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-10 rounded-2xl shadow-xl text-center max-w-md w-full">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-emerald-600 mb-2">{t('register.success.title')}</h2>
          <p className="text-gray-600">{t('register.success.redirecting')}</p>
          {planNote && <p className="text-sm text-gray-500 mt-3">{planNote}</p>}
        </div>
      </div>
    );
  }

  // Compute field errors only after submission attempt
  const nameErr = submitted ? validateName(formData.name) : '';
  const emailErr = submitted ? validateEmail(formData.email) : '';
  const passwordErr = submitted ? validatePassword(formData.password) : '';
  const confirmErr = submitted ? validateConfirmPassword(formData.confirmPassword) : '';

  return (
    <div className="min-h-full bg-gradient-to-br from-[#36CCD8]/5 to-gray-50 py-4 px-4 flex flex-col justify-center">
      <div className="max-w-md mx-auto">
        {/* Logo — links to vecindus.com */}
        <div className="text-center mb-8">
          <a href="https://vecindus.com" className="inline-block cursor-pointer hover:opacity-80 transition-opacity">
            <div className="flex items-center gap-2 justify-center">
              <svg width="36" height="36" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                <rect width="40" height="40" rx="10" fill="#36CCD8"/>
                <path d="M20 8L6 22L12 28L20 20L28 28L34 22L20 8Z" fill="white"/>
                <path d="M20 16L12 24L16 28L20 24L24 28L28 24L20 16Z" fill="white" fillOpacity="0.4"/>
              </svg>
              <span className="text-2xl font-bold text-gray-900">Vecindus</span>
            </div>
          </a>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('register.title')}</h2>
            <p className="text-gray-500 mt-1">{t('register.subtitle')}</p>
          </div>

          {error && (
            <div role="alert" className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('auth.name')}</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-4 py-3 min-h-[48px] bg-gray-50 border rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500 ${
                    nameErr ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder={t('register.namePlaceholder')}
                />
              </div>
              {nameErr && <p role="alert" className="text-red-500 text-xs font-medium mt-1 ml-1">{nameErr}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-4 py-3 min-h-[48px] bg-gray-50 border rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500 ${
                    emailErr ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder={t('auth.emailPlaceholder')}
                />
              </div>
              {emailErr && <p role="alert" className="text-red-500 text-xs font-medium mt-1 ml-1">{emailErr}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('auth.password')}</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-14 py-3 bg-gray-50 border-2 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500 ${
                    passwordErr ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder={t('register.passwordMinPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-[#36CCD8] hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {passwordErr && <p role="alert" className="text-red-500 text-xs font-medium mt-1 ml-1">{passwordErr}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.confirmPassword')}</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-14 py-3 bg-gray-50 border-2 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500 ${
                    confirmErr ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-[#36CCD8] hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label={showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmErr && <p role="alert" className="text-red-500 text-xs font-medium mt-1 ml-1">{confirmErr}</p>}
            </div>

            {/* Optional unit setup */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <label className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                <input type="checkbox" checked={unitSetup || accountType === 'condo'} onChange={(e) => setUnitSetup(e.target.checked)} className="h-4 w-4 accent-[#36CCD8]" />
                {t('register.unit.setupNow')}
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('register.unit.setupLater')}</p>
            </div>

            {/* Condo Selection (Optional) */}
            <div>
              <fieldset>
                <legend className="block text-sm font-semibold text-gray-700 dark:text-gray-700 mb-2">{t('register.condo.legend')}</legend>
                <div className="grid grid-cols-1 gap-2">
                  <button type="button" aria-pressed={accountType === 'personal'} onClick={() => { setAccountType('personal'); clearCondo(); setCondoDropdownOpen(false); }} className={`w-full px-4 py-3 rounded-xl border text-left ${accountType === 'personal' ? 'border-[#36CCD8] bg-[#36CCD8]/10' : 'border-gray-200 bg-gray-50'} text-gray-900 dark:text-gray-900 font-medium`}>{t('register.condo.personal')} {accountType === 'personal' && '✓'}</button>
                  <button type="button" aria-pressed={accountType === 'condo'} onClick={() => { setAccountType('condo'); setCondoDropdownOpen(true); }} className={`w-full px-4 py-3 rounded-xl border text-left ${accountType === 'condo' ? 'border-[#36CCD8] bg-[#36CCD8]/10' : 'border-gray-200 bg-gray-50'} text-gray-900 dark:text-gray-900 font-medium`}>{t('register.condo.join')} {accountType === 'condo' && '✓'}</button>
                </div>
              </fieldset>
              
              {accountType === 'condo' && !selectedCondoName ? (
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <button
                    type="button"
                    onClick={() => setCondoDropdownOpen(!condoDropdownOpen)}
                    className="w-full pl-12 pr-4 py-3 min-h-[48px] bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-left text-gray-700 dark:text-gray-700"
                  >
                    {t('register.condo.personal')}
                  </button>
                  {condoDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                      <input
                        type="text"
                        name="condoSearch"
                        value={formData.condoSearch}
                        onChange={handleChange}
                        placeholder={t('register.condo.searchPlaceholder')}
                        className="w-full px-4 py-2 border-b border-gray-100 focus:outline-none text-gray-900 placeholder-gray-500"
                        autoFocus
                      />
                      {availableCondos.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400">
                          {formData.condoSearch ? t('register.condo.noResults') : t('register.condo.typeToSearch')}
                          <div className="mt-1 text-xs">{t('register.condo.askAdmin')}</div>
                          <div className="mt-2 text-xs text-gray-600">{t('register.condo.notExists')}</div>
                        </div>
                      ) : (
                        availableCondos.map((condo) => (
                          <button
                            key={condo.id}
                            type="button"
                            onClick={() => selectCondo(condo)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0"
                          >
                            <div className="font-medium text-gray-900">{condo.name}</div>
                            {condo.city && (
                              <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3" /> {condo.city}, {condo.country}
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : accountType === 'condo' ? (
                <div className="flex items-center justify-between px-4 py-3 bg-[#36CCD8]/5 border border-[#36CCD8]/20 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#36CCD8]" />
                    <div>
                      <div className="font-medium text-gray-900">{selectedCondoName}</div>
                      <div className="text-xs text-gray-500">{t('register.condo.selected')}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearCondo}
                    className="text-gray-400 hover:text-red-500 text-sm"
                  >
                    ✕
                  </button>
                </div>
              ) : null}
              {accountType === 'condo' && <p className="text-xs text-gray-400 mt-1">{t('register.condo.inviteNote')}</p>}
            </div>

            {/* Unit (only show if condo selected) */}
            {(formData.condoId || (unitSetup && accountType === 'personal')) && (
              <div className="space-y-4">
                {formData.condoId && (
                <div>
                  <label htmlFor="inviteCode" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('register.inviteCode')}
                  </label>
                  <input
                    type="text"
                    id="inviteCode"
                    name="inviteCode"
                    value={formData.inviteCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 min-h-[48px] bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500"
                    placeholder={t('register.inviteCodePlaceholder')}
                    required
                  />
                </div>
                )}
                <div>
                  <label htmlFor="unit" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('register.unitNumber')}
                  </label>
                  <div className="relative">
                    <Home className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      id="unit"
                      name="unit"
                      value={formData.unit}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3 min-h-[48px] bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 transition-all text-gray-900 placeholder-gray-500"
                      placeholder={t('register.unitNumberPlaceholder')}
                    />
                  </div>
                </div>
              </div>
            )}

            {unitSetup && accountType === 'personal' && (
              <div className="space-y-3 rounded-xl border border-gray-200 p-4">
                <p className="text-sm font-semibold text-gray-700">{t('register.address.title')}</p>
                {(['street','number','neighborhood','locality','postal_code','country'] as const).map((field) => (
                  <input key={field} name={field} value={(formData as any)[field]} onChange={handleChange} placeholder={t(`register.address.${field}`)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900" required />
                ))}
              </div>
            )}
            {unitSetup && <PlanSelector value={planChoice} onChange={setPlanChoice} disabled={loading} />}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-[#36CCD8] hover:bg-[#2DB8C3] text-white font-bold text-lg rounded-xl shadow-lg shadow-[#36CCD8]/25 hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  {t('register.creating')}
                </>
              ) : (
                <>
                  {t('register.submit')} <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {waVisible && (
            <a
              href="https://wa.me/15717126394?text=Quiero%20crear%20mi%20cuenta%20Vecindus"
              target="_blank"
              rel="noreferrer"
              className="mt-4 w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1FBE59] text-white font-semibold py-3 min-h-[48px] rounded-xl transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.3 14.1c-.2.6-1.2 1.2-1.7 1.2-.5.1-1 .1-1.7-.1-2.7-1-4.4-3.7-4.5-3.9-.1-.2-1-1.4-1-2.6 0-1.2.6-1.8.9-2 .2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .6l-.4.6c-.1.2-.3.4-.1.7.2.3.7 1.2 1.5 1.9 1 .9 1.9 1.2 2.2 1.3.3.1.4.1.6-.1l.8-1c.2-.2.4-.2.6-.1l1.9.9c.3.1.5.2.5.3.1.2.1.7-.2 1.1Z"/>
              </svg>
              Regístrate por WhatsApp
            </a>
          )}

          <div className="text-center mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              {t('register.haveAccount')}{' '}
              <Link to="/login" className="font-semibold text-[#36CCD8] hover:text-[#2DB8C4] transition-colors">
                {t('register.loginHere')}
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-8">
          {t('login.footer.copyright')}
        </p>
      </div>
    </div>
  );
}
